// ============= Imports =============

import {
  AuthContextProps,
  tokenManager,
  formatCurrencyValue,
} from '@tw/tw-component-library';
import { PaymentWorkflowType } from '../../../components/container/forms/types/api/graph.types';
import { RequestData, sectionKeyToDisplay } from '../../../types';

// ============= Constants =============
const BASE_URL = import.meta.env.VITE_BASE_URL;
const API_ENDPOINT = '/api/Workflow/all';
const AXIS_SCOPE = 'api://f23d5e95-70a8-4aca-b373-e23d8c9f6454/access_as_user';
const HEADERS = {};

/**
 * Creates headers for API request
 * @param token Authentication token
 * @returns Headers object
 */
const createHeaders = (token: string, spoofedUser: string | null) => ({
  Authorization: `Bearer ${token}`,
  ...HEADERS,
  ...(spoofedUser && {
    'x-Spoof-User': spoofedUser,
  }),
});

// ============= Main Function =============
export interface GetAllWorkflowsParams {
  searchTerm?: string;
  status?: string;
  workflowType?: PaymentWorkflowType;
  currentlyAssigned?: boolean;
  sortDescending?: boolean;
  sortColumn?: string;
}

/**
 * Fetches workflows from Microsoft API with optional filtering
 * @param getAccessToken Function to acquire access token
 * @param spoofedUser Spoofed user email for testing
 * @param pageNumber Page number for pagination
 * @param pageSize Number of items per page
 * @param filters Optional filter parameters
 * @returns Promise with API response containing workflow data or error
 */
export const getAllWorkflows = async (
  getAccessToken: AuthContextProps['getAccessToken'],
  spoofedUser: string | null,
  pageNumber: number = 1,
  pageSize: number = 20,
  filters?: GetAllWorkflowsParams
) => {
  try {
    console.log('🔄 Fetching API data...');
    const token = await getAccessToken([AXIS_SCOPE]);
    if (!token) {
      throw new Error('Failed to acquire API token');
    }
    tokenManager.logTokenInfo(token, 'API');

    const searchParams = new URLSearchParams({
      pageNumber: String(pageNumber),
      pageSize: String(pageSize),
    });

    // Add filter parameters if provided
    if (filters) {
      if (filters.searchTerm) {
        searchParams.append('searchTerm', filters.searchTerm);
      }
      if (filters.status) {
        searchParams.append('status', filters.status);
      }
      if (filters.workflowType) {
        searchParams.append('workflowType', String(filters.workflowType));
      }
      if (filters.currentlyAssigned !== undefined) {
        searchParams.append(
          'currentlyAssigned',
          String(filters.currentlyAssigned)
        );
      }
      if (filters.sortDescending !== undefined) {
        searchParams.append('sortDescending', String(filters.sortDescending));
      }
      if (filters.sortColumn) {
        searchParams.append('sortColumn', filters.sortColumn);
      }
    }

    const url = `${BASE_URL}${API_ENDPOINT}?${searchParams.toString()}`;
    const convertURI = encodeURI(url);
    console.log('🌐 Making request to:', convertURI);

    const response = await fetch(convertURI, {
      method: 'GET',
      headers: createHeaders(token, spoofedUser || null),
    });

    if (!response.ok) {
      console.log(`HTTP error! status: ${response.status}`);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    const manipulatedItems: RequestData[] = (data.items || []).map(
      (item: RequestData) => ({
        ...item,
        status:
          sectionKeyToDisplay[
            item.status as keyof typeof sectionKeyToDisplay
          ] || item.status,
        paymentTotal: formatCurrencyValue(item.paymentTotal, 'GBP', false),
      })
    );
    console.log('✅ API data fetched successfully');

    return {
      success: true,
      data: {
        items: manipulatedItems,
        totalCount: data.totalCount ?? manipulatedItems.length,
        pageNumber: data.pageNumber ?? pageNumber,
        pageSize: data.pageSize ?? pageSize,
        totalPages: data.totalPages ?? 1,
      },
    };
  } catch (error) {
    console.error('🔴 Error fetching API data:', error);
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unknown error occurred',
    };
  }
};
